import { AxiosError } from 'axios';
import qs from 'qs';
import ApiClient from 'services/axios-config';

import SearchAssessmentsRequest, { SearchAssessmentsQueryParams } from './types/Assessment/SearchAssessmentsRequest';
import SearchLeadersRequest from './types/Assessment/SearchLeadersRequest';
import { SearchFoldersQueryParams } from './types/Folder/FoldersRequest';
import { SearchLFoldersResponse } from './types/Folder/FoldersResponse';
import { BatchReportJobStatusRequest } from './types/Report/BatchReportJobStatusRequest';
import { BatchReportJobStatusResponse } from './types/Report/BatchReportJobStatusResponse';
import GenerateBatchReportsRequest from './types/Report/GenerateBatchReportsRequest';
import { GenerateBulkBatchReportsResponse } from './types/Report/GenerateBulkBatchReportsResponse';
import GenerateBulkReportsRequest from './types/Report/GenerateBulkReportsRequest';
import { GetAllBatchReportJobsRequest } from './types/Report/GetAllBatchReportJobsRequest';
import { GetAllBatchReportJobsResponse } from './types/Report/GetAllBatchReportJobsResponse';
import { GetDownloadReportRequest } from './types/Report/GetDownloadReportRequest';

const lpiApi = '/lpi/api/v4';

export const AdminService = {
  Assessment: {
    SearchAssessments: async (
      queryParams: SearchAssessmentsQueryParams,
      request: SearchAssessmentsRequest,
    ): Promise<SearchAssessmentsResponse> => {
      const { organizationId, orderBy, offset, size } = queryParams;

      const query = [
        `offset=${offset}`,
        `size=${size}`,
        `orderBy=${orderBy}`,
        `organizationId=${organizationId}`,
      ].join('&');
      try {
        const { data } = await ApiClient.client.post(
          `lpi/api/v4/lpi-assessments/search?${query}`,
          {
            ...request,
            paramsSerializer: (params: any) => {
              return qs.stringify(params, { arrayFormat: 'comma' });
            },
          });

        return data;
      } catch (error: unknown) {
        const axiosError = error as AxiosError;
        throw axiosError;
      }
    },
  },
  Leader: {
    SearchLeaders: async (request: SearchLeadersRequest): Promise<SearchLeadersResponse> => {
      try {
        if (request.keyword) {
          request.keyword = request.keyword.replace(/\s+/g, ','); // Replace spaces with commas
        }
        if (request.email) {
          request.email = encodeURIComponent(request.email);
        }
        const { data } = await ApiClient.client.get(`${lpiApi}/lpi-leaders/search`, {
          params: request,
          paramsSerializer: (params) => qs.stringify(params, { encode: false }),
        });
        return data;
      } catch (error: unknown) {
        const axiosError = error as AxiosError;
        throw axiosError;
      }
    },
  },
  Report: {
    GetAllBatchReportJobs: async (request: GetAllBatchReportJobsRequest): Promise<GetAllBatchReportJobsResponse> => {
      const { queryParams } = request;
      const query = [
        `offset=${queryParams.offset}`,
        `size=${queryParams.size}`,
        `orderBy=${queryParams.orderBy}`,
        `organizationId=${queryParams.organizationId}`,
      ].join('&');
      try {
        const { data } = await ApiClient.client.get(`/api/v4/batch-reports/job?${query}`, {
          baseURL: window.env.PUBLIC_BATCH_REPORTS_API_BASE_URL,
        });

        return data;
      } catch (error: unknown) {
        const axiosError = error as AxiosError;
        throw axiosError;
      }
    },
    GetBatchReportJobStatus: async (request: BatchReportJobStatusRequest): Promise<BatchReportJobStatusResponse> => {
      const { pathParams } = request;
      const { queryParams } = request;

      try {
        const { data } = await ApiClient.client.get(`/api/v4/batch-reports/${pathParams.jobId}/status`, {
          baseURL: window.env.PUBLIC_BATCH_REPORTS_API_BASE_URL,
          params: { organizationId: queryParams.organizationId },
        });

        return data;
      } catch (error: unknown) {
        const axiosError = error as AxiosError;
        throw axiosError;
      }
    },
    GenerateBatchReports: async (request: GenerateBatchReportsRequest): Promise<{ url: string }> => {
      try {
        const normalizedPath = request.path.startsWith('/reports/')
          ? request.path
          : `/reports/${request.path.replace(/^\/+/, '')}`;

        const { data } = await ApiClient.client.post('/api/v4/batch-reports', {
          ...request,
          path: normalizedPath,
          settings: {
            selector: 'section',
            styles: {
              width: 6.5,
              height: 8.5,
              scale: 1,
            },
          },
        },
        {
          baseURL: window.env.PUBLIC_BATCH_REPORTS_API_BASE_URL,
        });

        return data;
      } catch (error: unknown) {
        const axiosError = error as AxiosError;
        throw axiosError;
      }
    },
    GetDownloadReportZip: async (request: GetDownloadReportRequest): Promise<{ url: string }> => {
      const { pathParams } = request;
      const { queryParams } = request;

      try {
        const { data } = await ApiClient.client.get(
          `/api/v4/batch-reports/${pathParams.jobId}/download?organizationId=${queryParams.organizationId}`,
          {
            baseURL: window.env.PUBLIC_BATCH_REPORTS_API_BASE_URL,
          });

        return data;
      } catch (error: unknown) {
        const axiosError = error as AxiosError;
        throw axiosError;
      }
    },
    GenerateBulkReports: async (request: GenerateBulkReportsRequest): Promise<GenerateBulkBatchReportsResponse> => {
      try {
        const organizationId = request?.queryParams?.organizationId;
        const payload = request?.payload ?? {};

        const { data } = await ApiClient.client.post('/api/v4/batch-reports/bulk', payload, {
          baseURL: window.env.PUBLIC_BATCH_REPORTS_API_BASE_URL,
          params: { organizationId },
          headers: { 'Content-Type': 'application/json' },
        });

        return data;
      } catch (error: unknown) {
        const axiosError = error as AxiosError;
        throw axiosError;
      }
    },
  },
  Folder: {
    SearchFolders: async (
      queryParams: SearchFoldersQueryParams,
    ): Promise<SearchLFoldersResponse> => {
      try {
        const {
          offset = 0,
          size = 20,
          hasCompletedLeadersAssessments = true,
          orderBy = '+Name',
          organizationId,
          keyword,
          reportType,
        } = queryParams;

        const query = [
          `offset=${offset}`,
          `size=${size}`,
          `hasCompletedLeadersAssessments=${hasCompletedLeadersAssessments}`,
          `orderBy=${orderBy}`,
          `organizationId=${organizationId}`,
          `keyword=${keyword || ''}`,
          ...(reportType ? [`reportType=${reportType}`] : []),
        ].join('&');
        const { data } = await ApiClient.client.get(`${lpiApi}/lpi-folders/search?${query}`);
        return data;
      } catch (error: unknown) {
        const axiosError = error as AxiosError;
        throw axiosError;
      }
    },
  },
  Company: {
    GetOrganizationUserRoles: async (userId: number): Promise<OrganizationUserRole> => {
      const queryParams = `organizationRole=OrgAdmin&userId=${userId}&size=30`;

      try {
        const { data } = await ApiClient.client.get(`api/v4/organization-user-roles?${queryParams}`,
          {
            baseURL: window.env.PUBLIC_COMPANY_API_BASE_URL,
          },
        );

        const matchedOrg = data.items.find((item: OrganizationUserRole) =>
          item.organizationName?.toLowerCase().includes('lpiorganizaton'),
        );

        return matchedOrg;
      } catch (error: unknown) {
        const axiosError = error as AxiosError;
        throw axiosError;
      }
    },
    GetOrganization: async (organizationId: number): Promise<Organization> => {
      try {
        const { data } = await ApiClient.client.get(`api/v4/organizations/${organizationId}`,
          {
            baseURL: window.env.PUBLIC_COMPANY_API_BASE_URL,
          },
        );

        return data;
      } catch (error: unknown) {
        const axiosError = error as AxiosError;
        throw axiosError;
      }
    },
    GetAccountOrganization: async (organizationId: number): Promise<number> => {
      const queryParams = `organizationId=${organizationId}`;

      try {
        const { data } = await ApiClient.client.get(`api/v4/account-organizations?${queryParams}`,
          {
            baseURL: window.env.PUBLIC_COMPANY_API_BASE_URL,
          },
        );

        return data.items[0]?.accountId;
      } catch (error: unknown) {
        const axiosError = error as AxiosError;
        throw axiosError;
      }
    },
    GetAccount: async (accountId: number): Promise<Account> => {
      try {
        const { data } = await ApiClient.client.get(`api/v4/accounts/${accountId}`,
          {
            baseURL: window.env.PUBLIC_COMPANY_API_BASE_URL,
          },
        );

        return data;
      } catch (error: unknown) {
        const axiosError = error as AxiosError;
        throw axiosError;
      }
    },
  },
  Image: {
    UploadImage: async (uploadImageRequest: UploadImageRequest, organizationId: number): Promise<UploadImageResponse> => {
      try {
        const { data } = await ApiClient.client.post(`${lpiApi}/lpi-images/organization/${organizationId}/save-logo`,
          {
            imageType: uploadImageRequest.imageType || 'jpeg',
            imageData: uploadImageRequest.imageData,
          },
        );

        return data;
      } catch (error: unknown) {
        const axiosError = error as AxiosError;
        throw axiosError;
      }
    },
    GetImage: async (fileName: string): Promise<{ fileName: string, url: string }> => {
      try {
        const { data } = await ApiClient.client.get(`api/v4/images/${fileName}/`,
          {
            baseURL: window.env.PUBLIC_IMAGE_API_BASE_URL,
          },
        );

        return data;
      } catch (error: unknown) {
        const axiosError = error as AxiosError;
        throw axiosError;
      }
    },
  },
};
